import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/StatusPill';
import { fmtDateTimeIST } from '@/lib/format';
import { Send, Copy, Trash2, AlertTriangle } from 'lucide-react';

// Sprint 11.6 (#8) — Reusable stage action bar shown at the top of each
// stage's tab content (Video Bot, MCQ). Surfaces:
//   • "Send link" CTA (opens the existing generate-link dialog)
//   • A list of outstanding/recent links for THIS stage with status badges
//   • "Copy link" + "Revoke" inline actions per link
//   • A "Locked" amber banner when the candidate is on hold / rejected
export function StageActionBar({
  stageCode, stageLabel, locked, lockedReason,
  configWarning = null, configBlocked = false,
  canMutate = true,
  onSend, links, onCopy, onRevoke, onReissue, testIdPrefix,
}) {
  return (
    <Card className="card-elevated" data-testid={`${testIdPrefix}-action-bar`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">{stageLabel} actions</p>
            <p className="text-xs text-muted-foreground">
              {canMutate
                ? `Send a single-use ${stageLabel} link or copy it to deliver out-of-band (WhatsApp, SMS, etc).`
                : `Issued ${stageLabel} links for this evaluation (read-only).`}
            </p>
          </div>
          {canMutate && (
            <Button
              size="sm"
              className="gap-1.5"
              disabled={locked || configBlocked}
              onClick={onSend}
              data-testid={`${testIdPrefix}-send-link`}
            >
              <Send className="h-3.5 w-3.5" /> Send {stageLabel} link
            </Button>
          )}
        </div>
        {configWarning && (
          <div
            className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/40 p-2 text-[11px] text-amber-800"
            data-testid={`${testIdPrefix}-config-warning`}
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{configWarning}</span>
          </div>
        )}
        {locked && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/40 p-2 text-[11px] text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5" />
            Candidate is {lockedReason} — new {stageLabel} links can&apos;t be generated. Reopen via the status pill in the header.
          </div>
        )}
        <div className="rounded-lg border bg-secondary/30 p-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Issued links ({links.length})
          </p>
          {links.length === 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">No links issued yet.</p>
          ) : (
            <ul className="mt-1 space-y-1.5" data-testid={`${testIdPrefix}-links-list`}>
              {links.map((l) => {
                const isTerminal = l.status === 'consumed' || l.status === 'revoked' || l.status === 'expired'
                  || l.progress_status === 'abandoned' || l.progress_status === 'completed';
                const canRevoke = l.status !== 'consumed' && l.status !== 'revoked' && l.progress_status !== 'abandoned'
                  && l.progress_status !== 'completed';
                const expiresFmt = fmtDateTimeIST(l.expires_at);
                const deliveryLabel = l.delivery_method === 'email'
                  ? 'Emailed'
                  : l.delivery_method === 'manual_copy'
                    ? 'Copied'
                    : l.delivery_method === 'both'
                      ? 'Emailed + copied'
                      : 'Not yet delivered';
                const deliveryClass = l.delivery_method
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-amber-200 bg-amber-50 text-amber-800';
                const progressLabel = l.progress_status
                  ? null
                  : (l.delivery_method ? 'Sent' : 'Awaiting delivery');
                const progressStatus = l.finalize_reason === 'auto_timer'
                  ? 'auto_submitted'
                  : l.progress_status;
                return (
                  <li
                    key={l.id}
                    className="flex flex-wrap items-center gap-2 rounded-md border bg-card p-2"
                    data-testid={`${testIdPrefix}-link-${l.id}`}
                  >
                    <span className="flex-1 text-xs">
                      <span className="text-muted-foreground">expires {expiresFmt}</span>
                    </span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${deliveryClass}`}>
                      {deliveryLabel}
                    </span>
                    {progressStatus ? (
                      <StatusPill status={progressStatus} size="sm" />
                    ) : (
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                        {progressLabel}
                      </span>
                    )}
                    {(l.status === 'revoked' || l.status === 'expired') && progressStatus !== 'revoked' && (
                      <StatusPill status={l.status} size="sm" />
                    )}
                    {canMutate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2"
                        onClick={() => onCopy(l)}
                        disabled={isTerminal}
                        data-testid={`${testIdPrefix}-copy-link-${l.id}`}
                      >
                        <Copy className="h-3.5 w-3.5" /> Copy
                      </Button>
                    )}
                    {canMutate && canRevoke && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2 text-rose-700 hover:bg-rose-50"
                        onClick={() => onRevoke(l.id)}
                        data-testid={`${testIdPrefix}-revoke-link-${l.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Revoke
                      </Button>
                    )}
                    {canMutate && onReissue && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2"
                        disabled={locked}
                        onClick={() => onReissue(l.id)}
                        data-testid={`${testIdPrefix}-reissue-link-${l.id}`}
                      >
                        Reissue
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
